import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiLock, FiEye } from 'react-icons/fi';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-6 transition-colors"
          >
            <FiArrowLeft />
            Retour à l'accueil
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold">
            Politique de Confidentialité
          </h1>
          <p className="text-gray-300 mt-4 text-lg">
            Dernière mise à jour : 19 janvier 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        
        {/* Introduction */}
        <section className="mb-12">
          <div className="bg-gray-50 border-l-4 border-gray-900 p-6 rounded-r-lg mb-8">
            <p className="text-gray-700 leading-relaxed">
              Chez <strong>ORIMA STORE</strong>, nous prenons très au sérieux la protection 
              de vos données personnelles. Cette politique de confidentialité vous informe 
              sur la manière dont nous collectons, utilisons et protégeons vos informations.
            </p>
          </div>
        </section>

        {/* Section 1 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            1. Données Collectées
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Lors de votre navigation et de vos achats sur notre site, nous collectons 
            les informations suivantes :
          </p>
          
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FiShield className="text-blue-600" />
                Informations d'identification
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Nom complet</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Adresse de livraison</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FiLock className="text-green-600" />
                Informations de commande
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Historique des achats</li>
                <li>Produits consultés</li>
                <li>Panier d'achat</li>
                <li>Préférences de livraison</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FiEye className="text-purple-600" />
                Données de navigation
              </h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Adresse IP</li>
                <li>Type de navigateur</li>
                <li>Pages visitées</li>
                <li>Durée de visite</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            2. Utilisation des Données
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Les données collectées sont utilisées pour :
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Traiter et gérer vos commandes</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Assurer la livraison de vos produits</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Vous contacter concernant votre commande</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Améliorer nos services et votre expérience d'achat</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Vous envoyer des offres promotionnelles (avec votre consentement)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold mt-1">✓</span>
              <span>Prévenir la fraude et assurer la sécurité du site</span>
            </li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            3. Protection des Données
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées 
            pour protéger vos données personnelles contre :
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">🔒 Accès non autorisé</p>
              <p className="text-sm text-gray-600">
                Accès sécurisé et chiffré à nos systèmes
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">🛡️ Perte de données</p>
              <p className="text-sm text-gray-600">
                Sauvegardes régulières et redondance
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">🔐 Utilisation abusive</p>
              <p className="text-sm text-gray-600">
                Contrôles d'accès stricts
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">⚡ Modification</p>
              <p className="text-sm text-gray-600">
                Journalisation et surveillance
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            4. Partage des Données
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Nous ne vendons, n'échangeons ni ne louons vos données personnelles à des tiers. 
            Vos informations peuvent être partagées uniquement avec :
          </p>
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Partenaires de livraison</h3>
              <p className="text-gray-600 text-sm">
                Pour assurer la livraison de vos commandes (nom, adresse, téléphone)
              </p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Prestataires de services</h3>
              <p className="text-gray-600 text-sm">
                Hébergement web, outils d'analyse (données anonymisées)
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Autorités légales</h3>
              <p className="text-gray-600 text-sm">
                Si requis par la loi ou pour protéger nos droits
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            5. Vos Droits
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Conformément à la loi 09-08 relative à la protection des données personnelles 
            au Maroc, vous disposez des droits suivants :
          </p>
          <div className="bg-gray-900 text-white p-6 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xl">👁️</span>
              <div>
                <p className="font-semibold mb-1">Droit d'accès</p>
                <p className="text-sm text-gray-300">
                  Consulter les données que nous détenons sur vous
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl">✏️</span>
              <div>
                <p className="font-semibold mb-1">Droit de rectification</p>
                <p className="text-sm text-gray-300">
                  Corriger vos données inexactes ou incomplètes
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl">🗑️</span>
              <div>
                <p className="font-semibold mb-1">Droit de suppression</p>
                <p className="text-sm text-gray-300">
                  Demander l'effacement de vos données
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-xl">🚫</span>
              <div>
                <p className="font-semibold mb-1">Droit d'opposition</p>
                <p className="text-sm text-gray-300">
                  Vous opposer au traitement de vos données
                </p>
              </div>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mt-4">
            Pour exercer ces droits, contactez-nous à : <strong>contact@orima.ma</strong>
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            6. Cookies
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Notre site utilise des cookies pour améliorer votre expérience de navigation. 
            Les cookies sont de petits fichiers texte stockés sur votre appareil.
          </p>
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Types de cookies utilisés :</h3>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site</li>
              <li><strong>Cookies de performance :</strong> Analyser l'utilisation du site</li>
              <li><strong>Cookies fonctionnels :</strong> Mémoriser vos préférences</li>
            </ul>
            <p className="text-sm text-gray-600 mt-4">
              Vous pouvez désactiver les cookies dans les paramètres de votre navigateur.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            7. Conservation des Données
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Nous conservons vos données personnelles aussi longtemps que nécessaire pour :
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-4">
            <li>Traiter vos commandes et assurer le service après-vente</li>
            <li>Respecter nos obligations légales (facturation, garantie)</li>
            <li>Résoudre d'éventuels litiges</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            Passé ce délai, vos données sont supprimées ou anonymisées.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            8. Modifications
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Nous nous réservons le droit de modifier cette politique de confidentialité 
            à tout moment. Toute modification sera publiée sur cette page avec une nouvelle 
            date de mise à jour. Nous vous encourageons à consulter régulièrement cette page.
          </p>
        </section>

        {/* Contact */}
        <section className="mb-12 bg-gray-900 text-white p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Contact</h2>
          <p className="mb-4">
            Pour toute question concernant cette politique de confidentialité ou vos données 
            personnelles, contactez-nous :
          </p>
          <div className="space-y-2 text-gray-300">
            <p>📧 Email : contact@orima.ma</p>
            <p>📱 WhatsApp : +212 6 65 07 80 92</p>
            <p>📍 ORIMA STORE - Casablanca, Maroc</p>
          </div>
        </section>

        {/* Back button */}
        <div className="text-center pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-800 transition-all"
          >
            <FiArrowLeft />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
